import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import DemoBanner from './components/DemoBanner';
import { isDemoMode } from './lib/demoMode';
import LandingPage from './pages/LandingPage';
import About from './pages/About';
import Dashboard from './pages/Dashboard';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Register from './pages/Register';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Contact from './pages/Contact';
// About page disabled - solo developer
// import About from './pages/About';
import FAQ from './pages/FAQ';
// Blog pages disabled for V1 - preserved for V2
// import Blog from './pages/Blog';
// import BlogPost from './pages/BlogPost';
// import BlogEditor from './pages/BlogEditor';
import AdminSetup from './pages/AdminSetup';
import AdminDashboard from './pages/AdminDashboard';
import Handoff from './pages/Handoff';
import VerificationLookup from './pages/VerificationLookup';
import PublicVerifyDetails from './pages/PublicVerifyDetails';
import Security from './pages/Security';
import Overview from './pages/Overview';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeErrorTracking, initializePerformanceMonitoring } from './lib/monitoring';
import { analytics } from './lib/analytics';
// Temporarily disabled due to React hooks conflict
// import A11yMenu from './components/A11yMenu';
import CookieConsent from './components/CookieConsent';
import { handleCookiePreferences } from './lib/cookies';

// Initialize monitoring in production
if (import.meta.env.PROD) {
  initializeErrorTracking();
  initializePerformanceMonitoring();
}

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // renamed from cacheTime in v5
    },
  },
});

// SEO and Analytics wrapper
function PageWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  useEffect(() => {
    // Only track page view if analytics cookies are accepted
    const analyticsEnabled = document.cookie.includes('analytics_cookies=true');
    if (analyticsEnabled) {
      analytics.trackPageView(location.pathname);
    }

    // Update document title based on route
    const titles: Record<string, string> = {
      '/': 'Agos Identity Verification | BSP eKYC Ready',
      '/pricing': 'Pricing | Agos Identity Verification',
      '/verify': 'Trust Portal | Public Record Verification',
      '/contact': 'Contact | Agos Identity Verification',
      '/admin': 'Admin Dashboard | Agos Identity Verification',
    };

    document.title = titles[location.pathname] || 'Agos Identity Verification';
  }, [location]);

  return <>{children}</>;
}

function Layout() {
  const location = useLocation();
  const isHandoff = location.pathname.startsWith('/handoff');

  return (
    <div className="min-h-screen flex flex-col bg-gray-900">
      {isDemoMode() && <DemoBanner />}
      {!isHandoff && <Navbar />}

      <main id="main-content" className={`flex-grow relative z-10 ${!isHandoff ? 'pt-16' : ''}`} tabIndex={-1}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          {/* Blog routes disabled for V1 */}
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/handoff/:sessionId" element={<Handoff />} />
          <Route path="/verify" element={<VerificationLookup />} />
          <Route path="/verify/:id" element={<PublicVerifyDetails />} />
          <Route path="/security" element={<Security />} />
          <Route path="/overview" element={<Overview />} />
        </Routes>
      </main>

      {!isHandoff && <Footer />}
      <CookieConsent onAccept={handleCookiePreferences} />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <PageWrapper>
            <Layout />
          </PageWrapper>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}